/**
 * Airlines / Car Rental — the vendor master list for one service type.
 *
 * One component for both tabs; `VENDOR_KINDS` carries what differs. Each row
 * links to the vendor's page, where its contacts and contracts are kept — the
 * place Vendor Relations was missing for everything that is not a hotel.
 *
 * Server-side search and paging, like Hotels: ~1,000 airlines is more than one
 * page, and a browser filter over a page looks like search while silently
 * searching a fraction.
 *
 * The status pills carry COUNTS from the server for the current search, so a
 * reader can see that a vendor exists but is inactive instead of concluding it
 * is missing. "Inactive" includes vendors that dropped out of Andavo's vendor
 * list — they are kept, with their contracts, and marked on the detail page.
 *
 * "With live contracts" narrows ~1,000 vendors to the few Vendor Relations has a
 * deal with. Search, filters and page live in the URL, so opening a vendor and
 * pressing Back returns to the same list, and a filtered view can be shared.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import {
  VENDOR_KINDS,
  type VendorListResult,
  type VendorListRow,
  type VendorType,
} from './vendor-kinds';

const PAGE_SIZE = 50;

type StatusFilter = 'active' | 'inactive' | 'all';
const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'all', label: 'All' },
];

export default function VendorsPage({ type }: { type: VendorType }) {
  const kind = VENDOR_KINDS[type];
  const t = kind.testId;

  const [sp, setSp] = useSearchParams();
  const debounced = (sp.get('q') ?? '').trim();
  const statusParam = sp.get('status');
  const status: StatusFilter = STATUS_OPTIONS.some(
    (o) => o.value === statusParam,
  )
    ? (statusParam as StatusFilter)
    : 'active';
  const withContracts = sp.get('contracts') === '1';
  const page = Math.max(1, parseInt(sp.get('page') ?? '1', 10) || 1);
  const [search, setSearch] = useState(debounced);
  const [data, setData] = useState<VendorListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * One writer for the URL. A new search or filter starts at page 1 — a stale
   * page number over a smaller result set would show an empty page that reads
   * as "no matches". `replace`, so typing does not fill the Back history.
   */
  const update = (
    patch: Partial<Record<'q' | 'status' | 'contracts' | 'page', string>>,
  ) => {
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        if (!('page' in patch)) next.delete('page');
        return next;
      },
      { replace: true },
    );
  };
  const setPage = (p: number) => update({ page: p > 1 ? String(p) : '' });

  useEffect(() => {
    const id = setTimeout(() => {
      if (search.trim() !== debounced) update({ q: search.trim() });
    }, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce on typing only
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      type,
      status,
      page: String(page),
      page_size: String(PAGE_SIZE),
    });
    if (debounced) params.set('q', debounced);
    if (withContracts) params.set('has_contracts', '1');
    apiFetch<VendorListResult>(`/api/btp/vendors?${params}`)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : String(e));
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [type, status, debounced, page, withContracts]);

  const total = data?.total ?? 0;
  const rows = data?.vendors ?? [];
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  const meta = useMemo(() => {
    if (loading) return 'Loading…';
    if (error) return '';
    if (total === 0) return `No ${kind.noun}s`;
    return `${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()} of ${total.toLocaleString()}`;
  }, [loading, error, total, rangeStart, rangeEnd, kind.noun]);

  return (
    <div className="page" data-testid={`${t}-page`}>
      <div className="page-header">
        <h1>{kind.title}</h1>
        <p className="page-intro">{kind.intro}</p>
      </div>

      <div className="section">
        <div className="section-header">
          <span className="section-label">Vendor master</span>
          <span className="section-meta" data-testid={`${t}-meta`}>
            {meta}
          </span>
        </div>
        <div className="section-body">
          <div
            style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              marginBottom: 14,
            }}
          >
            <div style={{ flex: '1 1 280px', maxWidth: 420 }}>
              <input
                aria-label={`Search ${kind.title.toLowerCase()}`}
                className="search-input"
                data-testid={`${t}-search`}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={kind.searchPlaceholder}
                type="text"
                value={search}
              />
            </div>
            <div>
              <div className="field-label" style={{ marginBottom: 4 }}>
                Status
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {STATUS_OPTIONS.map((o) => (
                  <button
                    aria-pressed={status === o.value}
                    className={'pill' + (status === o.value ? ' active' : '')}
                    data-testid={`${t}-status-${o.value}`}
                    key={o.value}
                    onClick={() =>
                      update({ status: o.value === 'active' ? '' : o.value })
                    }
                    type="button"
                  >
                    {o.label}
                    {data ? ` (${data.counts[o.value].toLocaleString()})` : ''}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="field-label" style={{ marginBottom: 4 }}>
                Contracts
              </div>
              <button
                aria-pressed={withContracts}
                className={'pill' + (withContracts ? ' active' : '')}
                data-testid={`${t}-with-contracts`}
                onClick={() => update({ contracts: withContracts ? '' : '1' })}
                title="Only vendors with a pending or active contract that has not passed its end date"
                type="button"
              >
                With live contracts
                {data?.counts.with_contracts != null
                  ? ` (${data.counts.with_contracts.toLocaleString()})`
                  : ''}
              </button>
            </div>
          </div>

          {error && (
            <div
              className="banner banner-error"
              data-testid={`${t}-error`}
              role="alert"
            >
              {error}
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" data-testid={`${t}-table`}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>{kind.codeLabel}</th>
                  <th>{kind.altCodeLabel}</th>
                  <th>Primary contact</th>
                  <th
                    className="right"
                    title="Pending or active contracts that have not passed their end date"
                  >
                    Live contracts
                  </th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr className="empty">
                    <td colSpan={6}>Loading {kind.title.toLowerCase()}…</td>
                  </tr>
                )}
                {!loading && !error && rows.length === 0 && (
                  <tr className="empty" data-testid={`${t}-empty`}>
                    <td colSpan={6}>
                      {debounced
                        ? `No ${kind.noun} matches “${debounced}”.`
                        : withContracts
                          ? `No ${kind.noun} has a live contract yet.`
                          : `No ${kind.noun}s in this view yet.`}
                    </td>
                  </tr>
                )}
                {!loading &&
                  rows.map((v) => (
                    <tr data-testid={`${t}-row-${v.uid}`} key={v.uid}>
                      <td style={{ fontWeight: 600 }}>
                        <VendorLink type={type} vendor={v} />
                      </td>
                      <td className="mono">{v.code}</td>
                      <td className="mono">{v.code_alternate || '—'}</td>
                      <td>
                        {v.primary_contact_name ?? (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="right">{v.live_contracts}</td>
                      <td>
                        <VendorStatusBadge vendor={v} />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 14,
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}
            >
              {total > 0
                ? `Page ${page.toLocaleString()} of ${totalPages.toLocaleString()}`
                : ''}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className="pill"
                data-testid={`${t}-prev-page`}
                disabled={loading || page <= 1}
                onClick={() => setPage(Math.max(1, page - 1))}
                type="button"
              >
                Previous
              </button>
              <button
                className="pill"
                data-testid={`${t}-next-page`}
                disabled={loading || page >= totalPages}
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                type="button"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The row's link to the vendor page. Two literal templates rather than one built
 * from `kind.path`, because HotelSupplierLayout.spec.ts proves a `:uid` route is
 * reachable by finding `to={\`/hotel-supplier/<tab>/${` in this feature's source —
 * a computed prefix would be just as reachable and still fail that check.
 */
function VendorLink({
  type,
  vendor,
}: {
  type: VendorType;
  vendor: VendorListRow;
}) {
  const uid = encodeURIComponent(vendor.uid);
  return type === 'air' ? (
    <Link className="row-link" to={`/hotel-supplier/airlines/${uid}`}>
      {vendor.name}
    </Link>
  ) : (
    <Link className="row-link" to={`/hotel-supplier/car-rental/${uid}`}>
      {vendor.name}
    </Link>
  );
}

/**
 * Active / inactive / no longer in Andavo's list. The last is its own state: the
 * vendor did not become inactive, it disappeared from the source, and its
 * contracts are still here.
 */
export function VendorStatusBadge({
  vendor,
}: {
  vendor: Pick<VendorListRow, 'is_active' | 'removed_from_source_at'>;
}) {
  if (vendor.removed_from_source_at) {
    return (
      <span
        className="badge badge-amber badge-no-dot"
        title="No longer in Andavo’s vendor list. Kept so its contacts and contracts stay reachable."
      >
        Removed from source
      </span>
    );
  }
  return vendor.is_active ? (
    <span className="badge badge-green badge-no-dot">Active</span>
  ) : (
    <span className="badge badge-neutral badge-no-dot">Inactive</span>
  );
}
